import { createColumnHelper } from "@tanstack/react-table";
import { fetchAuthSession } from "aws-amplify/auth";
import { RegulifyService } from "../api/RegulifyService";
import { buildWsUrl } from "./wsUrl";
import {
  claimsColumn,
  pmidColumn,
  relevanceColumn,
  remarksColumn,
  safetyColumn,
  searchTermColumn,
  subjectPopulationColumn,
  titleColumn,
} from "../components/clinicalEval/Columns";

const columnHelper = createColumnHelper();

export const SHEET_RELEVANT_LITERATURES = "Relevant Literatures";
export const SHEET_APPRAISAL = "Appraisal";
export const SHEET_CLAIMS = "Claims";

const literatureColumns = [
  columnHelper.accessor("title", titleColumn),
  columnHelper.accessor((row) => row.pmid, pmidColumn),
  columnHelper.accessor((row) => row.searchTerm, searchTermColumn),
  columnHelper.accessor((row) => row.relevance, relevanceColumn),
];

export const claimsTableColumns = [
  columnHelper.accessor("title", titleColumn),
  columnHelper.accessor((row) => row.pmid, pmidColumn),
  columnHelper.accessor((row) => row.search_term, searchTermColumn),
  columnHelper.accessor((row) => row.subject_population, subjectPopulationColumn),
  columnHelper.accessor((row) => row.claims, claimsColumn),
  columnHelper.accessor((row) => row.safety, safetyColumn),
  columnHelper.accessor((row) => row.relevancy?.reasoning, remarksColumn),
];

const sheetsByStudy = new Map();

export function rememberClinicalExportSheet(studyName, key, table) {
  if (!studyName || !table) return;
  const current = sheetsByStudy.get(studyName) || {};
  sheetsByStudy.set(studyName, { ...current, [key]: table });
}

export function recallClinicalExportSheet(studyName, key) {
  return sheetsByStudy.get(studyName)?.[key] || null;
}

export function buildLiteratureTable(combinedResults) {
  const results = combinedResults || [];
  const firstNonEmptyData = results.find(
    (res) => Array.isArray(res.data) && res.data.length > 0,
  );
  const firstQueryResponses = firstNonEmptyData?.data?.[0]?.query_responses || [];
  const dynamicColumns = firstQueryResponses.map((col, index) =>
    columnHelper.accessor("c-" + index, {
      id: "c-" + index,
      cell: (info) => info.getValue(),
      header: () => (
        <span className="block w-full break-words whitespace-pre-line">
          {col.query}
        </span>
      ),
    }),
  );
  const data = [];
  results.forEach((combinedResult) => {
    (combinedResult.data || []).forEach((item) => {
      const dataItem = {
        title: item.title,
        pmid: item.pmid,
        relevance: item.relevancy === "no" ? "Not relevant" : "Relevant",
        searchTerm: combinedResult.search_term,
      };
      (item.query_responses || []).forEach((res, index) => {
        dataItem["c-" + index] = res.response;
      });
      data.push(dataItem);
    });
  });
  return { data, columns: [...literatureColumns, ...dynamicColumns] };
}

function scoreColumns(prefix, responses) {
  return responses.map((col, index) =>
    columnHelper.accessor(prefix + "-" + index, {
      id: prefix + "-" + index,
      cell: (info) => info.getValue(),
      header: () => (
        <span className="block w-full break-words whitespace-pre-line">
          {col?.question || ""}
        </span>
      ),
    }),
  ).concat([
    columnHelper.accessor((row) => row[prefix + "Score"], {
      id: prefix + "Score",
      cell: (info) => info.getValue(),
      header: () => (
        <span className="block w-full break-words whitespace-pre-line">
          Result (Low value - good)
        </span>
      ),
    }),
  ]);
}

function firstResponses(groups) {
  const firstValid =
    (groups || [])
      .flat()
      .find((item) => Array.isArray(item?.response) && item.response.length > 0) ||
    null;
  return firstValid?.response || [];
}

export function buildAppraisalTable({ updatedF1, updatedF2, f1Enabled, f2Enabled }) {
  const data = [];
  const groups = [];
  const f1Responses = f1Enabled ? firstResponses(updatedF1) : [];
  const f2Responses = f2Enabled ? firstResponses(updatedF2) : [];

  if (f1Enabled && f1Responses.length > 0) {
    groups.push(
      columnHelper.group({
        id: "f1",
        header: () => (
          <div>As per table F1 of IMDRF MDCE WG/N56 final:2019</div>
        ),
        columns: scoreColumns("f1", f1Responses),
      }),
    );
    (updatedF1 || []).forEach((f1Results) => {
      if (!Array.isArray(f1Results) || f1Results.length === 0) return;
      f1Results.forEach((item) => {
        const dataItem = {
          title: item?.title || "",
          pmid: item?.pmid || "",
          searchTerm: item?.search_term || "",
          f1Score: item?.f1_score ?? "",
        };
        (item?.response || []).forEach((res, index) => {
          dataItem["f1-" + index] = res?.selected_option ?? "";
        });
        data.push(dataItem);
      });
    });
  }

  if (f2Enabled && f2Responses.length > 0) {
    groups.push(
      columnHelper.group({
        id: "f2",
        header: () => (
          <div>As per table F2 of IMDRF MDCE WG/N56 final:2019</div>
        ),
        columns: scoreColumns("f2", f2Responses),
      }),
    );
    (updatedF2 || []).forEach((f2Results) => {
      if (!Array.isArray(f2Results) || f2Results.length === 0) return;
      if (f1Enabled && data.length > 0) {
        data.forEach((item) => {
          const match = f2Results.find((f2Item) => item.pmid === f2Item?.pmid);
          if (!match) return;
          (match.response || []).forEach((res, index) => {
            item["f2-" + index] = res?.selected_option ?? "";
          });
          item.f2Score = match?.f2_score ?? "";
        });
      } else {
        f2Results.forEach((item) => {
          const dataItem = {
            title: item?.title || "",
            pmid: item?.pmid || "",
            searchTerm: item?.search_term || "",
            f2Score: item?.f2_score ?? "",
          };
          (item?.response || []).forEach((res, index) => {
            dataItem["f2-" + index] = res?.selected_option ?? "";
          });
          data.push(dataItem);
        });
      }
    });
  }

  return {
    data,
    columns: [...literatureColumns.slice(0, 3), ...groups],
  };
}

function requestWebSocket(path, payload, isResult) {
  return new Promise(async (resolve, reject) => {
    const session = await fetchAuthSession();
    const token = session.tokens?.idToken?.toString();
    const ws = new WebSocket(buildWsUrl(path, token));
    let keepAlive;
    let settled = false;
    const finish = (fn, value) => {
      if (settled) return;
      settled = true;
      clearInterval(keepAlive);
      try {
        ws.close();
      } catch {
        // already closed
      }
      fn(value);
    };
    ws.onopen = () => {
      keepAlive = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ type: "ping" }));
        }
      }, 20000);
      ws.send(JSON.stringify(payload));
    };
    ws.onmessage = (event) => {
      try {
        const parsed = JSON.parse(event.data);
        if (parsed.type === "error") {
          finish(reject, new Error(parsed.message || "Request failed"));
          return;
        }
        if (parsed.type === "info" || parsed.type === "pong") return;
        if (isResult(parsed)) finish(resolve, parsed);
      } catch {
        // ignore non-json keepalives
      }
    };
    ws.onerror = () =>
      finish(reject, new Error("There was a problem loading export data."));
    ws.onclose = () => {
      if (!settled) {
        finish(reject, new Error("Connection closed before export data arrived."));
      }
    };
  });
}

export async function loadLiteratureExportTable(studyName) {
  const cached = recallClinicalExportSheet(studyName, "literatures");
  if (cached) return cached;
  const response = await RegulifyService.cle_get_all_relevant_and_unique_results(studyName);
  if (!response?.data) {
    throw new Error("Relevant literatures are not available to export.");
  }
  const table = buildLiteratureTable(response.data.combined_results || []);
  rememberClinicalExportSheet(studyName, "literatures", table);
  return table;
}

export async function loadAppraisalExportTable(studyName, f1Enabled, f2Enabled) {
  const cached = recallClinicalExportSheet(studyName, "appraisal");
  if (cached) return cached;
  await RegulifyService.get_allowed_credits(5);
  const parsed = await requestWebSocket(
    "generate_clinical_appraisal_suitability_table",
    {
      product_name: studyName,
      f1_method: f1Enabled ? 1 : 0,
      f2_method: f2Enabled ? 1 : 0,
    },
    (message) => "updated_f1" in message || "updated_f2" in message,
  );
  const table = buildAppraisalTable({
    updatedF1: parsed.updated_f1 || [],
    updatedF2: parsed.updated_f2 || [],
    f1Enabled,
    f2Enabled,
  });
  rememberClinicalExportSheet(studyName, "appraisal", table);
  return table;
}

export async function loadClaimsExportTable(studyName) {
  const cached = recallClinicalExportSheet(studyName, "claims");
  if (cached) return cached;
  await RegulifyService.get_allowed_credits(5);
  const parsed = await requestWebSocket(
    "extract_subject_claims_safety",
    { product_name: studyName, f1_method: 1, f2_method: 0 },
    (message) => Array.isArray(message?.all_extracted_data),
  );
  const table = {
    data: parsed.all_extracted_data,
    columns: claimsTableColumns,
  };
  rememberClinicalExportSheet(studyName, "claims", table);
  return table;
}
